import { apiClient } from './api-client';

// Analytics Query types
export interface AnalyticsQueryParams {
  startDate?: string;
  endDate?: string;
  limit?: number;
  /** Rows to skip (the gap list's "load more"). */
  offset?: number;
}

// Write-path payloads (search instrumentation)
export interface SearchLogPayload {
  query: string;
  channel?: 'portal' | 'chat' | 'email' | 'agent' | 'api';
  resultCount?: number;
  hasClick?: boolean;
  clickedArticleId?: string;
  clickPosition?: number;
  sessionId?: string;
}

export interface SearchClickPayload {
  sessionId: string;
  articleId: string;
  position?: number;
}

// Dashboard types
export interface AnalyticsDashboard {
  period: { start: string; end: string };
  search: {
    totalSearches: number;
    uniqueQueries: number;
    avgResultCount: number;
    zeroResultRate: number;
    clickThroughRate: number;
    trendDirection: 'up' | 'down' | 'stable';
    trendPercentage: number;
  };
  content: {
    totalArticles: number;
    totalViews: number;
    avgHelpfulnessScore: number;
    topPerformingCount: number;
    underperformingCount: number;
  };
  deflection: {
    rate: number;
    totalDeflected: number;
    estimatedSavings: number;
  };
  gaps: {
    totalOpen: number;
    highPriority: number;
    resolvedThisPeriod: number;
    coverageScore: number;
  };
  embeddings: {
    totalSources: number;
    embeddedCount: number;
    pendingCount: number;
    staleCount: number;
    healthScore: number;
  };
}

// Search Analytics types
export interface PopularSearch {
  query: string;
  count: number;
  avgResultCount: number;
  // API returns `clickRate` (0-1). `clickThroughRate` kept optional for back-compat.
  clickRate: number;
  clickThroughRate?: number;
}

export interface FailedSearch {
  query: string;
  count: number;
  lastSearchedAt: string;
  suggestedTopics?: string[];
}

export interface SearchTrend {
  date: string;
  searchCount: number;
  zeroResultCount: number;
  clickThroughRate: number;
}

// Article Effectiveness types
export interface ArticleEffectiveness {
  articleId: string;
  title: string;
  viewCount: number;
  helpfulCount: number;
  notHelpfulCount: number;
  helpfulnessScore: number;
  avgTimeOnPage: number;
  bounceRate: number;
}

// Deflection types
export interface DeflectionMetrics {
  deflectionRate: number;
  selfServed: number;
  escalated: number;
  estimatedSavings: number;
}

// Gap Analysis types
export interface KnowledgeGap {
  id: string;
  topic: string;
  searchCount: number;
  priority: number;
  status: 'open' | 'in_progress' | 'resolved';
  suggestedContent?: string;
  relatedArticles?: string[];
  firstDetected?: string; 
  lastOccurrence?: string;
}

export interface GapAnalysisReport {
  totalGaps: number;
  highPriorityGaps: number;
  coverageScore: number;
  topGaps: KnowledgeGap[];
  trendingTopics: string[];
  recommendations: string[];
}

export interface GapResolutionTrend {
  date: string;
  newGaps: number;
  resolvedGaps: number;
  totalOpen: number;
}

// Embedding types
/**
 * One row of `GET /knowledge-analytics/embeddings/status`.
 *
 * This was previously declared with a `status` field on an
 * `embedded | pending | stale | error` vocabulary. No such field is sent and
 * no such vocabulary exists: the payload carries `embeddingStatus` on the
 * queue's own `pending | processing | completed | failed`. Because the name
 * never matched, every read of it was `undefined` — which is why the Embeddings
 * KPIs all showed 0 while the list beneath them had rows in it, and why every
 * article carrying a report row rendered NOT INDEXED on the register.
 *
 * STALENESS IS NOT A STATUS. It is `needsRefresh`, computed server-side as
 * "never embedded, or not completed, or edited since it was last embedded".
 * A stale source is still `completed` — the assistant can answer from it, just
 * from the older text — so stale is a SUBSET of embedded rather than a rival
 * to it (knowledge-service #9103).
 */
export interface EmbeddingStatus {
  sourceId: string;
  sourceType: 'article' | 'document';
  title: string;
  embeddingStatus: 'pending' | 'processing' | 'completed' | 'failed';
  chunkCount: number;
  /** ISO date; absent until the first successful embedding. */
  lastEmbedded?: string;
  lastUpdated: string;
  needsRefresh: boolean;
}

export interface EmbeddingHealth {
  totalSources: number;
  embeddedCount: number;
  pendingCount: number;
  staleCount: number;
  errorCount: number;
  healthScore: number;
}

export const knowledgeAnalyticsService = {
  // Dashboard
  async getDashboard(days = 30): Promise<AnalyticsDashboard> {
    const response = await apiClient.get(`/knowledge-analytics/dashboard?days=${days}`);
    return response.data;
  },

  // Search Analytics
  async getPopularSearches(params?: AnalyticsQueryParams): Promise<PopularSearch[]> {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.limit) queryParams.append('limit', String(params.limit));

    const response = await apiClient.get(`/knowledge-analytics/search/popular?${queryParams.toString()}`);
    return response.data;
  },

  async getFailedSearches(params?: AnalyticsQueryParams): Promise<FailedSearch[]> {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.limit) queryParams.append('limit', String(params.limit));

    const response = await apiClient.get(`/knowledge-analytics/search/failed?${queryParams.toString()}`);
    return response.data;
  },

  async getSearchTrends(params?: AnalyticsQueryParams): Promise<SearchTrend[]> {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const response = await apiClient.get(`/knowledge-analytics/search/trends?${queryParams.toString()}`);
    return response.data;
  },

  // Article Effectiveness
  async getArticleEffectiveness(params?: AnalyticsQueryParams): Promise<ArticleEffectiveness[]> {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.limit) queryParams.append('limit', String(params.limit));

    const response = await apiClient.get(`/knowledge-analytics/articles/effectiveness?${queryParams.toString()}`);
    return response.data;
  },

  async getTopPerformingArticles(limit = 10): Promise<ArticleEffectiveness[]> {
    const response = await apiClient.get(`/knowledge-analytics/articles/top-performing?limit=${limit}`);
    return response.data;
  },

  async getUnderperformingArticles(limit = 10): Promise<ArticleEffectiveness[]> {
    const response = await apiClient.get(`/knowledge-analytics/articles/underperforming?limit=${limit}`);
    return response.data;
  },

  // Deflection Metrics
  async getDeflectionMetrics(params?: AnalyticsQueryParams): Promise<DeflectionMetrics> {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const response = await apiClient.get(`/knowledge-analytics/deflection?${queryParams.toString()}`);
    return response.data;
  },

  // Gap Analysis
  async identifyGaps(params?: AnalyticsQueryParams): Promise<KnowledgeGap[]> {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.limit) queryParams.append('limit', String(params.limit));
    if (params?.offset) queryParams.append('offset', String(params.offset));

    const response = await apiClient.get(`/knowledge-analytics/gaps?${queryParams.toString()}`);
    return response.data;
  },

  async getGapAnalysisReport(params?: AnalyticsQueryParams): Promise<GapAnalysisReport> {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const response = await apiClient.get(`/knowledge-analytics/gaps/report?${queryParams.toString()}`);
    return response.data;
  },

  async getSuggestedTopics(limit = 10): Promise<string[]> {
    const response = await apiClient.get(`/knowledge-analytics/gaps/suggested-topics?limit=${limit}`);
    return response.data;
  },

  /** Knowledge gap resolution trends (new/resolved/open per day). */
  async getGapResolutionTrends(days = 30): Promise<GapResolutionTrend[]> {
    const response = await apiClient.get(`/knowledge-analytics/gaps/trends?days=${days}`);
    return response.data;
  },

  /** Mark a knowledge gap as resolved */
  async resolveGap(gapId: string, articleId?: string): Promise<{ success: boolean }> {
    const response = await apiClient.post(`/knowledge-analytics/gaps/${gapId}/resolve`, { articleId });
    return response.data;
  },

  /** Dismiss a knowledge gap (mark as not actionable) */
  async dismissGap(gapId: string, reason?: string): Promise<{ success: boolean }> {
    const response = await apiClient.post(`/knowledge-analytics/gaps/${gapId}/dismiss`, { reason });
    return response.data;
  },

  // Embedding Analytics
  async getEmbeddingStatus(sourceType?: 'article' | 'document'): Promise<EmbeddingStatus[]> {
    const params = sourceType ? `?sourceType=${sourceType}` : '';
    const response = await apiClient.get(`/knowledge-analytics/embeddings/status${params}`);
    return response.data;
  },

  async getEmbeddingHealth(): Promise<EmbeddingHealth> {
    const response = await apiClient.get('/knowledge-analytics/embeddings/health');
    return response.data;
  },

  /**
   * Re-embed exactly these sources (phase 25 §2.5's `RE-INDEX NOW →`).
   *
   * The band names a count, so the action has to act on that count — passing
   * `sourceIds` rather than a blanket `sourceType` refresh is what keeps the
   * two honest. A "re-index everything" button under a "4 articles" heading is
   * a different, much slower operation wearing the same label.
   */
  async reindex(sourceIds: string[]): Promise<{ queued?: number }> {
    const response = await apiClient.post('/knowledge-analytics/embeddings/refresh-bulk', {
      sourceIds,
      sourceType: 'article',
    });
    return response.data ?? {};
  },

  // ---- Write paths (instrumentation) ----

  /** Record a search so it feeds the analytics dashboard (popular/failed/trends/gaps). */
  async logSearch(payload: SearchLogPayload): Promise<void> {
    await apiClient.post('/knowledge-analytics/search/log', payload);
  },

  /** Record that a result was clicked, for click-through / deflection metrics. */
  async logSearchClick(payload: SearchClickPayload): Promise<void> {
    await apiClient.post('/knowledge-analytics/search/click', payload);
  },
};

export default knowledgeAnalyticsService;
